import React, {useEffect, useState} from "react";
import AppBar from "../components/app_bar/AppBar";
import Drawer from "../components/Drawer";
import { 
    Typography, Container, CssBaseline, Box, Toolbar, Grid, Link,
    Divider, Chip,
} from '@mui/material';
import { useParams, useLocation } from "react-router-dom";
import FlagIcon from '@mui/icons-material/Flag';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faYoutube, faBilibili, faXTwitter } from '@fortawesome/free-brands-svg-icons';

import * as CONSTANTS from "../constants";
import NotFound from "./NotFound";

import { view_organization } from "../services/topic_view";

const OrganizationDetails = () => {

    document.title = CONSTANTS.TITLE

    const location = useLocation();

    const [organization, setOrganization] = useState(null);
    const {lang, organizationId} = useParams();

    const [drawerOpen, setDrawerOpen] = useState(false);
            
    const handleDrawerOpen = (open) => {
        if (open === undefined) {
            setDrawerOpen(!drawerOpen)
        } else {
            setDrawerOpen(open);
        }
    }

    const fetchOrganization = async () => {
        const [status, result] = await view_organization(lang, organizationId.toString());
        if (status === 200) {
            setOrganization(result);
        }
    }

    useEffect(() => {
        fetchOrganization();
    }, [location.pathname, location.search]);

    if (organization) {

        document.title = `${organization.name} | ${CONSTANTS.DRAWER.ORGANIZATION[lang]} - Wotagei Zone`

        let region = '';
        CONSTANTS.COUNTRY_LIST.forEach(country => {
            if (country.code === organization.region) {
                region = country.label[lang];
                return;
            }
        });
        return (
            <Box>
                <CssBaseline />
                <AppBar lang={lang} handleDrawerOpen={handleDrawerOpen}/>
                <Box sx={{ display: 'flex' }}>
                    <Drawer lang={lang} drawerOpen={drawerOpen} setDrawerOpen={setDrawerOpen} />
                    <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
                        <Toolbar />
                        <Container maxWidth="md">
                            {/* Breadcrumb and Title */}
                            <Typography>
                                <Link
                                    href={`/${lang}/organization`}
                                    underline="hover"
                                    color="textPrimary"
                                >
                                    {CONSTANTS.DRAWER.ORGANIZATION[lang]}
                                </Link>
                                {` > ${organization.name}`}
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Typography 
                                variant="h4" 
                                gutterBottom 
                                sx={{
                                    textAlign: { xs: 'center', md: 'left' },
                                    fontWeight: 700
                                }}
                                mt={4}
                                mb={4}
                            >
                                {organization.name}
                            </Typography>

                            {/* Main Info Section */}
                            <Box sx={{ mb: 4 }}>
                                <Grid container spacing={3}>
                                    {/* Country */}
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <FlagIcon />
                                            <Typography variant="subtitle2" fontWeight={600}>{CONSTANTS.WOTAGEISHI_FILTER.COUNTRY[lang]}:</Typography>
                                            <Typography variant="body2">{region}</Typography>
                                        </Box>
                                    </Grid>
                                    {/* Markings */}
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                            <BookmarkIcon />
                                            <Typography variant="subtitle2" fontWeight={600}>{CONSTANTS.WOTAGEISHI_DETAILS.MARKINGS[lang]}:</Typography>
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                                                {organization.markings.map((marking) => (
                                                    <Chip
                                                        key={marking.toString()} 
                                                        label={marking.toString()}
                                                        variant="outlined"
                                                        sx={{
                                                            borderRadius: '0.5rem',
                                                            borderColor: '#ccc',
                                                            fontSize: '0.85rem',
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    </Grid>
                                    {/* Team Leader */}
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <PersonIcon />
                                            <Typography variant="subtitle2" fontWeight={600}>{CONSTANTS.ORGANIZATION_DETAILS.TEAM_LEADER[lang]}:</Typography>
                                            <Typography variant="body2">{organization.team_leader.join(', ')}</Typography>
                                        </Box>
                                    </Grid>
                                    {/* Famous Members */}
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <PeopleIcon />
                                            <Typography variant="subtitle2" fontWeight={600}>{CONSTANTS.ORGANIZATION_DETAILS.KEY_MEMBERS[lang]}:</Typography>
                                            <Typography variant="body2">{organization.key_members.join(', ')}</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>
                            <Divider sx={{ my: 2 }} />

                            {/* Social Media Section */}
                            <Box sx={{ mt: 4 }}>
                                <Typography variant="h6" fontWeight={700} mb={2}>
                                    {CONSTANTS.WOTAGEISHI_DETAILS.SOCIAL_MEDIA[lang]}
                                </Typography>
                                <Grid container spacing={2}>
                                    {/* X (Twitter) */}
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <FontAwesomeIcon icon={faXTwitter} size="lg" />
                                            <Typography variant="subtitle2" fontWeight={600}>X</Typography>
                                        </Box>
                                        <Link 
                                            href={organization.official_account.x}
                                            rel="noreferrer"
                                            target="_blank"
                                            sx={{ wordBreak: 'break-all' }}
                                        >
                                            <Typography 
                                                color="textPrimary"
                                                sx={{ 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis', 
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word',
                                                }}
                                                maxWidth="100%"
                                            >
                                                {organization.official_account.x}
                                            </Typography>
                                        </Link>
                                    </Grid>
                                    {/* YouTube */}
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <FontAwesomeIcon icon={faYoutube} size="lg" />
                                            <Typography variant="subtitle2" fontWeight={600}>YouTube</Typography>
                                        </Box>
                                        <Link 
                                            href={organization.official_account.youtube}
                                            rel="noreferrer"
                                            target="_blank"
                                            sx={{ wordBreak: 'break-all' }}
                                        >
                                            <Typography 
                                                color="textPrimary"
                                                sx={{ 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis', 
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word',
                                                }}
                                                maxWidth="100%"
                                            >
                                                {organization.official_account.youtube}
                                            </Typography>
                                        </Link>
                                    </Grid>
                                    {/* Bilibili */}
                                    <Grid item xs={12} md={4}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <FontAwesomeIcon icon={faBilibili} size="lg" />
                                            <Typography variant="subtitle2" fontWeight={600}>Bilibili</Typography>
                                        </Box>
                                        <Link 
                                            href={organization.official_account.bilibili}
                                            rel="noreferrer"
                                            target="_blank"
                                            sx={{ wordBreak: 'break-all' }}
                                        >
                                            <Typography 
                                                color="textPrimary"
                                                sx={{ 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis', 
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word',
                                                }}
                                                maxWidth="100%"
                                            >
                                                {organization.official_account.bilibili}
                                            </Typography>
                                        </Link>
                                    </Grid>
                                </Grid>
                            </Box>
                            <Divider sx={{ my: 2 }} />
                        </Container>
                    </Box>
                </Box>
            </Box>
        );
    }
    
};
 
export default OrganizationDetails;